import cloud from 'd3-cloud';
import tinycolor2 from 'tinycolor2';
import util from '../util';
import Widget from './Widget';

const BASE_CSS = `
html, body {
	margin: 0;
	padding: 0;
	height: 100%;
	overflow: hidden;
}
.word-cloud-widget {
	position: absolute;
	inset: 0;
	overflow: hidden;
}
.wc-word {
	cursor: default;
	user-select: none;
}
`;

export default class WordCloudWidget extends Widget {
	constructor(opts) {
		super(opts);
		this.rawWords = [];

		this._refreshTimer    = null;
		this._renderScheduled = false;
		this._currentLayout   = null;
		this._renderToken     = 0;
	}

	get _widgetType() { return 'word-cloud'; }

	_build() {
		this._injectCSS('wordCloudBaseCSS', BASE_CSS);

		this.cloudElem = document.createElement('div');
		this.cloudElem.className = 'word-cloud-widget';
		this.container.appendChild(this.cloudElem);

		this._resizeObserver = new ResizeObserver(() => this._scheduleRender());
		this._resizeObserver.observe(this.cloudElem);
	}

	async init() {
		await super.init();
		this._refreshWords();
	}

	destroy() {
		clearTimeout(this._refreshTimer);
		this._resizeObserver?.disconnect();
		this._cancelLayout();
		this._renderToken++;
		document.getElementById('googleFontLink')?.remove();
		super.destroy();
	}

	applySettings(settings) {
		super.applySettings(settings);
		this._scheduleRender();
	}

	_generateCSS() {
		const s = this.settings;
		this._updateFontLink();
		const textDecoration = [
			s.underlineText     ? 'underline'    : null,
			s.strikethroughText ? 'line-through' : null,
		].filter(Boolean).join(' ');
		const shadowFilter = (s.blurRadius > 0 || s.spreadRadius > 0)
			? util.generateSpreadShadowFilter(s.spreadRadius, s.blurRadius, s.shadowColor)
			: null;
		this._setCSS('wordCloudStyle', `
			.wc-word {
				${s.letterSpacing > 0 ? `letter-spacing: ${s.letterSpacing}px;` : ''}
				${textDecoration  ? `text-decoration: ${textDecoration};`        : ''}
				${s.uppercaseText ? 'text-transform: uppercase;'                 : ''}
				${shadowFilter    ? `filter: ${shadowFilter};`                   : ''}
			}
			${s.extraCSS || ''}
		`);
	}

	_cancelLayout() {
		if(this._currentLayout) {
			this._currentLayout.stop();
			this._currentLayout = null;
		}
	}

	async _refreshWords() {
		clearTimeout(this._refreshTimer);
		try {
			const raw = await this.api.get(`widget/${encodeURIComponent(this.hash)}/words`);
			this.rawWords = raw || [];
			this._scheduleRender();
		} catch(e) {
			console.error('word-cloud: failed to load words', e);
		}
		const intervalMs = (this.settings.refreshInterval || 10) * 60 * 1000;
		this._refreshTimer = setTimeout(() => this._refreshWords(), intervalMs);
	}

	_scheduleRender() {
		if(this._renderScheduled) return;
		this._renderScheduled = true;
		requestAnimationFrame(() => {
			this._renderScheduled = false;
			this._render();
		});
	}

	_getColor(colorWeight) {
		const base = this.settings.textColor || '#ffffff';
		const c = tinycolor2(base);
		const alpha = 0.4 + colorWeight * 0.6;
		return c.setAlpha(c.getAlpha() * alpha).toRgbString();
	}

	_scaleWeight(t) {
		switch(this.settings.scale || 'sqrt') {
			case 'log':    return t <= 0 ? 0 : Math.log(t * Math.E);
			case 'linear': return t;
			default:       return Math.sqrt(t);
		}
	}

	async _render() {
		const token = ++this._renderToken;

		const W = this.cloudElem.clientWidth;
		const H = this.cloudElem.clientHeight;
		if(!W || !H || !this.rawWords.length) return;

		this._cancelLayout();

		const s = this.settings;
		const maxWords   = s.maxWords    || 50;
		const minFont    = s.minFontSize || 14;
		const maxFont    = s.maxFontSize || 80;
		const fontFamily = s.fontFamily  || 'sans-serif';
		const fontWeight = s.boldText    ? 'bold'   : 'normal';
		const fontStyle  = s.italicText  ? 'italic' : 'normal';

		if(s.fontFamily && !s.customFont) {
			try {
				await document.fonts.load(`${fontWeight} ${fontStyle} ${maxFont}px '${s.fontFamily}'`);
			} catch(e) { /* fall back to system font */ }
			if(token !== this._renderToken) return;
		}

		const wordSlice = this.rawWords.slice(0, maxWords);
		const minN  = wordSlice.reduce((m, w) => Math.min(m, w.n), Infinity);
		const maxN  = wordSlice.reduce((m, w) => Math.max(m, w.n), -Infinity);
		const range = maxN - minN || 1;

		const words = wordSlice.map(w => {
			const t = (w.n - minN) / range;
			const scaled = this._scaleWeight(t);
			return {
				text:        w.word,
				size:        Math.round(minFont + scaled * (maxFont - minFont)),
				colorWeight: t,
			};
		});

		const orientations = Math.max(1, Math.round(s.rotationOrientations || 2));
		const minAngle = parseFloat(s.rotationMinAngle ?? 0);
		const maxAngle = parseFloat(s.rotationMaxAngle ?? -90);
		const angles = orientations === 1
			? [minAngle]
			: Array.from({ length: orientations }, (_, i) =>
				minAngle + (maxAngle - minAngle) * i / (orientations - 1));

		this._currentLayout = cloud()
			.size([W, H])
			.words(words)
			.padding(4)
			.spiral(s.spiral || 'archimedean')
			.font(fontFamily)
			.fontWeight(fontWeight)
			.fontStyle(fontStyle)
			.fontSize(d => d.size)
			.rotate(() => angles[Math.floor(Math.random() * angles.length)])
			.on('end', placed => this._draw(placed, W, H))
			.start();
	}

	_draw(placed, W, H) {
		this._currentLayout = null;
		this.cloudElem.innerHTML = '';
		if(!placed.length) return;

		const NS = 'http://www.w3.org/2000/svg';
		const svg = document.createElementNS(NS, 'svg');
		svg.setAttribute('width', W);
		svg.setAttribute('height', H);
		svg.style.cssText = 'position:absolute;inset:0';

		const g = document.createElementNS(NS, 'g');
		g.setAttribute('transform', `translate(${W / 2},${H / 2})`);

		for(const word of placed) {
			const text = document.createElementNS(NS, 'text');
			text.setAttribute('class', 'wc-word');
			text.setAttribute('text-anchor', 'middle');
			text.setAttribute('transform', `translate(${word.x},${word.y})rotate(${word.rotate})`);
			text.style.fontSize   = word.size + 'px';
			text.style.fontFamily = word.font;
			text.style.fontWeight = word.weight;
			text.style.fontStyle  = word.style;
			text.style.fill       = this._getColor(word.colorWeight);
			text.textContent = word.text;
			g.appendChild(text);
		}

		svg.appendChild(g);
		this.cloudElem.appendChild(svg);
	}
}
